import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { usePopper } from 'react-popper';
import {
  Button,
  Indicator,
  Popover,
  PopoverButton,
  StyledPopoverPanel,
  PopoverItem,
  Divider,
  Spinner,
} from '@crazyegginc/hatch';

import { useModal, useSite, usePermissions } from '/src/hooks';
import { Modal } from '/src/contexts/modal';
import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { FEATURES, ROOT_ELEMENT_ID } from '/src/features/_global/constants';

const validationSchema = yup.object().shape({
  site: yup.mixed().required('Please select a site.'),
});

export function SiteSelectorModal({ onCancel, initialSite }) {
  const modal = useModal();
  const { sites, loadingSites, selectSite } = useSite({
    dontSelectSite: true,
  });
  const permissions = usePermissions();
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  const disabled = loadingSites;

  const [referenceElement, setReferenceElement] = useState(null);
  const [tooltipElement, setTooltipElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, tooltipElement, {
    placement: 'bottom',
    modifiers: [
      useMemo(
        () => ({
          name: 'widthModifier',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
          },
        }),
        [],
      ),
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: 'bottom',
          fallbackPlacements: 'bottom',
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
      useMemo(
        () => ({
          name: 'offset',
          options: {
            offset: () => {
              return [0, 5];
            },
          },
        }),
        [],
      ),
    ],
  });

  return (
    <Modal dialogClassName="!pt-5" disableOutsideClick={true} dismissable={false}>
      <Formik
        initialValues={{ site: initialSite ?? null }}
        onSubmit={(values) => {
          selectSite(values.site);
        }}
        validationSchema={validationSchema}
      >
        {({ values, errors, setFieldValue }) => (
          <Form>
            <Modal.Title>Select a site</Modal.Title>
            <div>
              <div className="text-body-2 mb-5 block">Choose which website you want to create your Funnel for:</div>
              <div className="w-full">
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <div ref={setReferenceElement}>
                        <PopoverButton
                          className={classNames(
                            'relative h-[35px] w-full cursor-default rounded border border-mystic-500 pl-3 pr-9 text-left',
                            'text-body-4 leading-none',
                            'focus:border-dodger-blue-500 focus:outline-none',
                            {
                              '!border-dodger-blue-500': open,
                              'cursor-not-allowed bg-white-lilac-500 text-lynch-500': disabled,
                              'bg-white text-black-pearl-500': !disabled,
                            },
                          )}
                          disabled={disabled}
                          aria-label="site selector"
                        >
                          {loadingSites ? (
                            <div className="flex items-center space-x-2.5">
                              <Spinner />
                              <span className="text-lynch-500">loading sites...</span>
                            </div>
                          ) : (
                            <span>{values.site?.name ?? 'Select your site'}</span>
                          )}
                          <Indicator type="dropdown" className="absolute right-4 top-1/2 -translate-y-1/2" />
                        </PopoverButton>
                      </div>
                      {createPortal(
                        <StyledPopoverPanel
                          className="!max-h-[280px] !overflow-y-auto !z-[100000]"
                          ref={setTooltipElement}
                          style={{ ...styles.popper }}
                          {...attributes.popper}
                        >
                          {({ close }) => (
                            <>
                              {sites.length ? (
                                sites.map((site) => (
                                  <PopoverItem
                                    key={site.id}
                                    onClick={() => {
                                      setFieldValue('site', site);
                                      close();
                                    }}
                                  >
                                    {site.name}
                                  </PopoverItem>
                                ))
                              ) : (
                                <div className="text-body-5 relative inline-block w-full max-w-full rounded px-2.5 py-1.25 text-left leading-[1.15]">
                                  You don’t currently have any sites.
                                </div>
                              )}
                              {canEditSites ? (
                                <>
                                  <Divider className="!-mx-4 !my-2 !w-auto" />
                                  <PopoverItem
                                    className="!font-semibold !text-dodger-blue-500"
                                    onClick={() => {
                                      close();
                                      modal.show(
                                        <AddSiteModal
                                          onExtraSteps={(newSite) => {
                                            setTimeout(() => selectSite(newSite), 200);
                                          }}
                                        />,
                                      );
                                    }}
                                  >
                                    Add a new site
                                  </PopoverItem>
                                </>
                              ) : null}
                            </>
                          )}
                        </StyledPopoverPanel>,
                        document.querySelector(`#${ROOT_ELEMENT_ID}`),
                      )}
                    </>
                  )}
                </Popover>
              </div>
              {errors?.site ? <div className="text-error">{errors.site}</div> : null}
            </div>

            <Modal.Actions>
              <Button type="submit" disabled={loadingSites}>
                Create New Funnel
              </Button>
              <Modal.Cancel onClick={onCancel} />
            </Modal.Actions>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
